'use client';

import { Box, Link as MuiLink, Typography } from '@mui/material';
import Link from 'next/link';
import React from 'react';

export interface FooterProps {
  onPartnerLoginClick?: () => void;
}

const FooterLink: React.FC<{
  href: string;
  children: React.ReactNode;
  external?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}> = ({ href, children, external, onClick }) => {
  if (external) {
    return (
      <MuiLink
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        sx={{
          color: '#888',
          fontSize: '13px',
          textDecoration: 'none',
          '&:hover': { color: '#4fc3f7' },
        }}
      >
        {children}
      </MuiLink>
    );
  }

  return (
    <MuiLink
      component={Link}
      href={href}
      onClick={onClick}
      sx={{
        color: '#888',
        fontSize: '13px',
        textDecoration: 'none',
        '&:hover': { color: '#4fc3f7' },
      }}
    >
      {children}
    </MuiLink>
  );
};

export const Footer: React.FC<FooterProps> = ({ onPartnerLoginClick }) => {
  return (
    <Box
      component="footer"
      sx={{
        mt: 6,
        mb: 0,
        backgroundColor: '#0a0a0f',
        borderTop: '1px solid #1a1a2e',
      }}
    >
      <Box
        sx={{
          px: { xs: 3, md: 6 },
          py: 4,
          maxWidth: 1280,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        {/* Column 1 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            Marketplace
          </Typography>
          <FooterLink href="/">Browse Services</FooterLink>
          <FooterLink href="/for-buyers">For Buyers</FooterLink>
          <FooterLink href="/about">About Aithon</FooterLink>
        </Box>

        {/* Column 2 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            For Partners & Suppliers
          </Typography>
          <FooterLink href="/for-partners">For Partners & Suppliers</FooterLink>
          <FooterLink href="/for-agents">For AI Agents</FooterLink>
          {onPartnerLoginClick ? (
            <FooterLink
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                onPartnerLoginClick();
              }}
            >
              Partner Login
            </FooterLink>
          ) : (
            <FooterLink href="/login">Partner Login</FooterLink>
          )}
        </Box>

        {/* Column 3 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            Developers
          </Typography>
          <FooterLink href="/api/docs" external>
            API Documentation
          </FooterLink>
          <FooterLink href="/api/v1/mcp" external>
            MCP Integration
          </FooterLink>
          <FooterLink href="/.well-known/agents.json">agents.json</FooterLink>
        </Box>

        {/* Column 4 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Company</Typography>
          <FooterLink href="/about">About</FooterLink>
          <FooterLink href="/terms">Terms of Service</FooterLink>
          <FooterLink href="/privacy">Privacy Policy</FooterLink>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid #12121a',
          px: { xs: 3, md: 6 },
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#666',
          fontSize: '12px',
          maxWidth: 1280,
          mx: 'auto',
        }}
      >
        <Box>© 2026 Aithon Technologies</Box>
        <Box sx={{ opacity: 0.9 }}>Powered by Aithon</Box>
      </Box>
    </Box>
  );
};
